import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import request from "../utils/request";

const DOCUMENTS = {
  "ops-contract": {
    title: "Remote Contractor Engagement Agreement",
    description: "Primary engagement terms, confidentiality, and non-disparagement.",
    order: 1,
    content: `REMOTE CONTRACTOR ENGAGEMENT, PAYMENT, CONFIDENTIALITY, AND MUTUAL NON DISPARAGEMENT AGREEMENT

(For Remote Contractors Based in the Philippines)

This Agreement is entered into as of the Effective Date by and between:

Bellhopt LLC, a Wyoming incorporated profit corporation with its principal place of business at 28202 Cabot Rd Unit 391, Laguna Niguel, California 92677 ("Company"); and the undersigned Contractor.

1. BACKGROUND
1.1 Contractor will provide remote services to Company as an independent contractor, not as an employee.
1.2 The Parties have discussed Contractor's role, responsibilities, schedule, compensation, and performance expectations.
1.3 The Parties wish to clearly set expectations, protect Company and its founders, and avoid future disputes.

2. ENGAGEMENT STATUS
2.1 Contractor is engaged as an independent contractor. This Agreement does not create an employment relationship.
2.2 Contractor is solely responsible for all taxes, social security, and statutory payments required under applicable law.
2.3 Contractor is not entitled to Company employee benefits unless a separate written agreement expressly grants them.

3. TERM AND TERMINATION
3.1 The engagement begins on the Effective Date and continues until terminated.
3.2 The engagement is at will. Company may terminate at any time, with or without cause.
3.3 Contractor may terminate by providing written notice.
3.4 Company may immediately terminate if Contractor commits misconduct, violates confidentiality, or fails to comply with policies.

4. SERVICES AND SCHEDULE
4.1 Contractor will perform remote operations and support services including order handling, communication, ticket management, and reviews.
4.2 Schedule will be set by Company in writing. Example schedules may include Tuesday–Saturday or Sunday–Thursday.
4.3 Contractor agrees to follow all documented procedures and standard operating processes.

5. COMPENSATION AND TIMEKEEPING
5.1 Contractor will be paid per verified hours worked unless a different rate is agreed in writing.
5.2 Contractor must accurately track and record all working time.
5.3 No clock in early or late without approval. Extra hours require prior approval.

6. CONFIDENTIALITY
6.1 Confidential Information includes non-public info about Company, founders, guests, hosts, drivers, vendors, or partners.
6.2 Contractor agrees to use Confidential Information only for services and protect it.
6.3 Never place guest/host numbers in driver-visible fields; never store data to personal tools.
6.4 Confidentiality survives termination.

7. INTELLECTUAL PROPERTY
Work Product is Company-owned; Contractor assigns all rights.

8. NON DISPARAGEMENT
No false or disparaging statements about Company, founders, employees, or partners.

9. COMPLIANCE
Comply with policies including Handbook, Metrics, Communication Policy, and Tone Guide.

10. GOVERNING LAW
California law governs. Breaches may justify injunctive relief.

11. ACKNOWLEDGMENT
Contractor has read, understands, and signs voluntarily.`,
  },
  handbook: {
    title: "Operations Handbook & Rules",
    description: "Communication standards, onboarding requirements, and conduct rules.",
    order: 2,
    content: `BELLHOPT OPERATIONS HANDBOOK, RULES, COMMUNICATION, AND ONBOARDING

(For Remote Operations Contractors Based in the Philippines)

This Handbook describes how remote Operations contractors must work for Bellhopt.

CORE RULES AND NON-NEGOTIABLES (violations may cause write-ups, probation, or termination):
• No guest/host phone numbers in driver-visible fields or extra instructions.
• Operations handles all communication; no outside channels (WhatsApp, SMS, personal calls).
• VPN must always be on; secure networks only; no public WiFi.
• Professional environment: quiet, reliable headset/voice isolation.
• Accurate timekeeping; no early/late clock without approval.
• Use only internal docs/templates; no guesses or unverified entries.
• No drugs/alcohol on shift; no other jobs while clocked in.
• Follow SOPs and checklists; no skipping required steps.

COMMUNICATION GUIDELINES:
• Do not bombard guests/hosts/drivers; only request needed info.
• Use available data before asking.
• Formal tone, correct grammar, approved templates.

ONBOARDING AND TRAINING:
• Complete all modules: sign paperwork, set schedule, accept code of conduct.
• Setup checklist: admin account, VPN, password manager, Slack, Gmail, Dialpad, GigSmart, TaskRabbit.
• Provide docs before live shifts: signed engagement, handbook acknowledgment, payment details, emergency contact.

SCHEDULING AND AVAILABILITY:
• Schedules set by leadership.
• Responsibilities: Morning Review, active ticket/order comms, EOD Review.
• Extra hours require approval and correct logging.

SICK DAYS AND TIME OFF:
• May be limited and unpaid; last-minute only for emergencies.

PROHIBITED CONDUCT:
• Working under influence; working another job on shift; bypassing SOP/security; direct guest/host/driver contact.

CONSEQUENCES:
Coaching → write-ups → removal from shifts → probation → termination.

ACKNOWLEDGMENT:
I have read, understand, and will comply. Violations may result in loss of shifts or termination.`,
  },
  metrics: {
    title: "Metrics & Performance Expectations",
    description: "Metrics definitions, review cadence, and consequences.",
    order: 3,
    content: `BELLHOPT METRICS, PERFORMANCE, REVIEWS, AND CONSEQUENCES

This document explains how Bellhopt uses metrics, reviews, and expectations to manage Operations performance.

METRIC DEFINITIONS:
• Tickets Closed: Number of tickets resolved during shift.
• High Priority Tickets: Urgent tickets properly flagged and handled.
• Refund Rate: Percentage of orders refunded (high rate > 8% triggers review).
• Morning Review: Completion and quality of morning check.
• EOD Review: Completion and quality of end-of-day check.
• Gold Stars: Recognition for excellent performance.
• Errors in Reviews: Mistakes found during quality reviews.
• Response Rate: Speed of first response to tickets.
• Escalations: Issues elevated to leadership.

PRINCIPLES:
• Metrics are directional; trends matter.
• Context is considered; outliers start a conversation.
• Reviews are regular and transparent.

REVIEWS, RAISES, AND PROBATION:
• Weekly check-ins; quarterly reviews; written summaries.
• Raises depend on sustained strong performance, low errors, reliability, professionalism.
• If performance drops: coaching → focused plan (30–60 days) → probation → outcomes.

EXPECTATIONS:
• Service: fast, clear, polite; realistic expectations; follow-through.
• Metrics: keep in healthy range; raise issues early.
• Ownership: if you claim it, you own it until closed/handed off.
• Communication: status/issues/risks early; follow note/escalation formats.
• Quality: accuracy on codes/times/addresses/notes; use checklists.
• Learning: open to feedback, ask questions, grow.
• Daily: Morning Review on time; real-time updates; EOD complete.

ACKNOWLEDGMENT:
I have read, understand, and accept expectations. Failure to meet them over time may lead to coaching, plans, probation, or termination.`,
  },
};

const DOC_ORDER = Object.keys(DOCUMENTS).sort((a, b) => DOCUMENTS[a].order - DOCUMENTS[b].order);
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function SigningAssistant() {
  const navigate = useNavigate();
  const adminInfo = useSelector((s) => s.login.adminInfo);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  // Track which documents have been signed (persists during session)
  const [signedDocs, setSignedDocs] = useState({});
  const [currentDocId, setCurrentDocId] = useState(DOC_ORDER[0]);

  const [fields, setFields] = useState({
    fullName: "",
    email: "",
    role: "",
    startDate: "",
    today: todayStr(),
    acknowledgmentAccepted: false,
    sendCopyToUser: true,
    employeeId: "",
  });

  const [hasReadCurrentDoc, setHasReadCurrentDoc] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [typedSignature, setTypedSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lastSignedResult, setLastSignedResult] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);

  const allDocsSigned = DOC_ORDER.every((id) => signedDocs[id]);
  const signedCount = DOC_ORDER.filter((id) => signedDocs[id]).length;

  // Prefill user info
  useEffect(() => {
    if (!adminInfo) return;
    setFields((prev) => ({
      ...prev,
      fullName: `${adminInfo.firstName || ""} ${adminInfo.lastName || ""}`.trim() || prev.fullName,
      email: adminInfo.email || adminInfo.workEmail || prev.email,
      role: adminInfo.role || prev.role,
      employeeId: adminInfo._id || prev.employeeId,
    }));
  }, [adminInfo]);

  // Load existing signing status for this user to avoid re-signing
  useEffect(() => {
    if (!adminInfo) return;
    const loadStatus = async () => {
      try {
        const { data } = await request.get("/signing/status");
        const map = {};
        (data?.signedDocs || []).forEach((doc) => {
          if (doc?.docId) map[doc.docId] = doc;
        });
        setSignedDocs(map);

        const firstUnsigned = DOC_ORDER.find((id) => !map[id]);
        if (firstUnsigned) {
          setCurrentDocId(firstUnsigned);
          setHasReadCurrentDoc(false);
          setFields((prev) => ({ ...prev, acknowledgmentAccepted: false }));
        } else if (DOC_ORDER.length > 0) {
          // All signed; show first tab as signed
          setCurrentDocId(DOC_ORDER[0]);
          setHasReadCurrentDoc(true);
          setFields((prev) => ({ ...prev, acknowledgmentAccepted: true }));
        }
      } catch (err) {
        console.error("Failed to load signing status", err);
      } finally {
        setStatusLoaded(true);
      }
    };
    loadStatus();
  }, [adminInfo]);

  // Validation
  const canSubmit = useMemo(() => {
    const basicFields = fields.fullName && fields.email && fields.role && fields.today;
    return basicFields && hasReadCurrentDoc && fields.acknowledgmentAccepted && signatureDataUrl;
  }, [fields, hasReadCurrentDoc, signatureDataUrl]);

  // Canvas drawing setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    const start = (x, y) => {
      isDrawing.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x, y) => {
      if (!isDrawing.current) return;
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const end = () => {
      isDrawing.current = false;
      setSignatureDataUrl(canvas.toDataURL("image/png"));
    };

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (e.touches?.length) {
        return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
      }
      return { x: e.offsetX, y: e.offsetY };
    };

    const handleDown = (e) => { const { x, y } = getPos(e); start(x, y); };
    const handleMove = (e) => { const { x, y } = getPos(e); draw(x, y); };
    const handleUp = () => end();

    canvas.addEventListener("mousedown", handleDown);
    canvas.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseup", handleUp);
    canvas.addEventListener("mouseleave", handleUp);
    canvas.addEventListener("touchstart", handleDown);
    canvas.addEventListener("touchmove", handleMove);
    canvas.addEventListener("touchend", handleUp);

    return () => {
      canvas.removeEventListener("mousedown", handleDown);
      canvas.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseup", handleUp);
      canvas.removeEventListener("mouseleave", handleUp);
      canvas.removeEventListener("touchstart", handleDown);
      canvas.removeEventListener("touchmove", handleMove);
      canvas.removeEventListener("touchend", handleUp);
    };
  }, []);

  const handleTypedSignature = () => {
    if (!typedSignature) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = "italic 28px Georgia, serif";
    ctx.fillStyle = "#1e293b";
    ctx.fillText(typedSignature, 16, canvas.height / 2 + 8);
    setSignatureDataUrl(canvas.toDataURL("image/png"));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureDataUrl("");
    setTypedSignature("");
  };

  const handleSubmitCurrentDoc = async () => {
    setError("");
    // Front-end guard for required fields
    if (!fields.fullName?.trim()) return setError("Full legal name is required.");
    if (!fields.email?.trim()) return setError("Bellhopt email is required.");
    if (!fields.role?.trim()) return setError("Role / Title is required.");
    if (!fields.today?.trim()) return setError("Today's date is required.");
    if (!hasReadCurrentDoc) return setError("Please confirm you have read this document.");
    if (!fields.acknowledgmentAccepted) return setError("Please check the acknowledgment box.");
    if (!signatureDataUrl) return setError("Signature is required (draw or type).");

    setSubmitting(true);
    try {
      const doc = DOCUMENTS[currentDocId];
      const payload = {
        ...fields,
        agreementText: doc.content,
        docId: currentDocId,
        docTitle: doc.title,
        signatureDataUrl,
      };
      const { data } = await request.post("/signing/ops-guide", payload);
      setSignedDocs((prev) => ({ ...prev, [currentDocId]: data }));
      setLastSignedResult(data);

      // Find next unsigned doc
      const nextUnsigned = DOC_ORDER.find((id) => !signedDocs[id] && id !== currentDocId);
      if (nextUnsigned) {
        // Reset for next doc (keep signature and user info)
        setCurrentDocId(nextUnsigned);
        setHasReadCurrentDoc(false);
        setFields((prev) => ({ ...prev, acknowledgmentAccepted: false }));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Unable to submit. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectDoc = (docId) => {
    setCurrentDocId(docId);
    setHasReadCurrentDoc(!!signedDocs[docId]);
    setFields((prev) => ({ ...prev, acknowledgmentAccepted: !!signedDocs[docId] }));
    setError("");
  };

  const currentDoc = DOCUMENTS[currentDocId];
  const isCurrentDocSigned = !!signedDocs[currentDocId];

  // Completion screen
  if (allDocsSigned) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-emerald-100 p-8 text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">All Documents Signed!</h1>
            <p className="text-slate-600 mt-2">You have successfully signed all {DOC_ORDER.length} required documents. Copies have been sent to legal@bellhopt.com and to your email.</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2">
            <p className="text-sm font-semibold text-slate-700">Signed documents:</p>
            {DOC_ORDER.map((id) => (
              <div key={id} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{DOCUMENTS[id].title}</span>
                <a
                  href={signedDocs[id]?.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate("/tickets")}
            className="w-full px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
          >
            Go to Ticket Queue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50/30">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <header className="space-y-1">
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest">Bellhopt Onboarding</p>
          <h1 className="text-3xl font-bold text-slate-900">Sign Required Documents</h1>
          <p className="text-slate-600">
            Review and sign each document below. Progress: <span className="font-semibold text-indigo-600">{signedCount} of {DOC_ORDER.length}</span> completed.
          </p>
        </header>

        {/* Document tabs */}
        <div className="flex flex-wrap gap-2">
          {DOC_ORDER.map((id, idx) => {
            const doc = DOCUMENTS[id];
            const isSigned = !!signedDocs[id];
            const isCurrent = currentDocId === id;
            return (
              <button
                key={id}
                onClick={() => selectDoc(id)}
                className={`relative px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  isCurrent
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                    : isSigned
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-300"
                    : "bg-white text-slate-700 border-slate-200 hover:border-indigo-200"
                }`}
              >
                <span className="mr-2 font-bold">{idx + 1}.</span>
                {doc.title}
                {isSigned && (
                  <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-xs">✓</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Document viewer */}
            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{currentDoc.title}</h2>
                  <p className="text-sm text-slate-500">{currentDoc.description}</p>
                </div>
                {isCurrentDocSigned ? (
                  <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Signed</span>
                ) : hasReadCurrentDoc ? (
                  <span className="px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">Ready to sign</span>
                ) : (
                  <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">Read required</span>
                )}
              </div>
              <div className="h-80 overflow-y-auto p-5 bg-slate-50 text-sm leading-relaxed text-slate-800 whitespace-pre-line font-mono">
                {currentDoc.content}
              </div>
              {!isCurrentDocSigned && (
                <div className="px-5 py-3 border-t border-slate-100 bg-white flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasReadCurrentDoc}
                      onChange={(e) => setHasReadCurrentDoc(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    I have read and understood this document
                  </label>
                </div>
              )}
            </section>

            {/* Form fields (only show if not already signed) */}
            {!isCurrentDocSigned && (
              <>
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900">Your Information</h2>
                    <span className="text-xs text-slate-500">Required fields marked *</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Full legal name *" value={fields.fullName} onChange={(v) => setFields({ ...fields, fullName: v })} />
                    <Field label="Bellhopt email *" value={fields.email} onChange={(v) => setFields({ ...fields, email: v })} />
                    <Field label="Role / Title *" value={fields.role} onChange={(v) => setFields({ ...fields, role: v })} />
                    <Field label="Start date" type="date" value={fields.startDate} onChange={(v) => setFields({ ...fields, startDate: v })} />
                    <Field label="Today's date *" type="date" value={fields.today} onChange={(v) => setFields({ ...fields, today: v })} />
                    <label className="flex items-center gap-2 text-sm text-slate-700 col-span-full">
                      <input
                        type="checkbox"
                        checked={fields.sendCopyToUser}
                        onChange={(e) => setFields({ ...fields, sendCopyToUser: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Email me a signed copy
                    </label>
                  </div>
                </section>

                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
                  <h2 className="text-lg font-bold text-slate-900">Acknowledgment & Signature</h2>
                  <p className="text-sm text-slate-600">
                    I acknowledge that I have read and understood this document. I understand that this does not create a contract of employment, but describes current expectations and processes, which Bellhopt may update over time.
                  </p>
                  <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fields.acknowledgmentAccepted}
                      onChange={(e) => setFields({ ...fields, acknowledgmentAccepted: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="font-medium">I agree with the acknowledgment above</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Draw your signature</p>
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={160}
                        className="w-full max-w-xl h-[160px] border border-dashed border-slate-300 rounded-xl bg-slate-50 cursor-crosshair"
                      />
                      <button
                        type="button"
                        onClick={clearSignature}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Or type your name</p>
                      <input
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Type full name"
                      />
                      <button
                        type="button"
                        onClick={handleTypedSignature}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white hover:bg-slate-900"
                      >
                        Use typed signature
                      </button>
                    </div>
                  </div>
                </section>
              </>
            )}

            {/* Already signed message */}
            {isCurrentDocSigned && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-emerald-800">This document has been signed</p>
                  <p className="text-sm text-emerald-700">A copy was sent to legal@bellhopt.com and your email.</p>
                  <a
                    href={signedDocs[currentDocId]?.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-indigo-600 font-semibold hover:underline"
                  >
                    Download signed PDF →
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            {/* Progress */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Signing Progress</h3>
              <div className="space-y-2">
                {DOC_ORDER.map((id, idx) => {
                  const isSigned = !!signedDocs[id];
                  return (
                    <div key={id} className="flex items-center gap-2 text-sm">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isSigned ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"
                      }`}>
                        {isSigned ? "✓" : idx + 1}
                      </span>
                      <span className={isSigned ? "text-slate-700" : "text-slate-500"}>{DOCUMENTS[id].title}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${(signedCount / DOC_ORDER.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">{signedCount} of {DOC_ORDER.length} signed</p>
            </div>

            {/* Submit button */}
            {!isCurrentDocSigned && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-3">
                <button
                  onClick={handleSubmitCurrentDoc}
                  disabled={submitting || !canSubmit}
                  className={`w-full px-5 py-3.5 rounded-xl font-semibold text-white transition-all ${
                    canSubmit && !submitting
                      ? "bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg"
                      : "bg-slate-300 cursor-not-allowed"
                  }`}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Signing...
                    </span>
                  ) : (
                    `Sign "${currentDoc.title}"`
                  )}
                </button>
                {error && <p className="text-sm text-red-600 text-center">{error}</p>}
                {lastSignedResult && !isCurrentDocSigned && (
                  <p className="text-sm text-emerald-600 text-center">
                    ✓ Previous document signed. Continue with this one.
                  </p>
                )}
              </div>
            )}

            {/* Next document prompt */}
            {isCurrentDocSigned && signedCount < DOC_ORDER.length && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5">
                <p className="text-sm font-semibold text-indigo-800 mb-3">Next document</p>
                {DOC_ORDER.filter((id) => !signedDocs[id]).slice(0, 1).map((id) => (
                  <button
                    key={id}
                    onClick={() => selectDoc(id)}
                    className="w-full px-4 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
                  >
                    Sign {DOCUMENTS[id].title} →
                  </button>
                ))}
              </div>
            )}

            {/* Checklist */}
            {!isCurrentDocSigned && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Before signing</h3>
                <ChecklistItem label="Required fields filled" complete={!!(fields.fullName && fields.email && fields.role && fields.today)} />
                <ChecklistItem label="Document read" complete={hasReadCurrentDoc} />
                <ChecklistItem label="Acknowledgment checked" complete={fields.acknowledgmentAccepted} />
                <ChecklistItem label="Signature captured" complete={!!signatureDataUrl} />
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="text-sm space-y-1.5">
      <span className="text-slate-700 font-medium">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
    </label>
  );
}

function ChecklistItem({ label, complete }) {
  return (
    <div className="flex items-center gap-2 text-sm py-1">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
        complete ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"
      }`}>
        {complete ? "✓" : ""}
      </span>
      <span className={complete ? "text-slate-700" : "text-slate-500"}>{label}</span>
    </div>
  );
}
